import { publicContentAxios } from '@/lib/axiosInstance';
import {
  isSearchServiceAvailable,
  searchServiceAxios,
} from '@/lib/searchServiceAxios';
import {
  getCurrentTenantIdWithFallback,
  validateUserTenant,
} from '@/lib/tenant';
import {
  QuestionInfoType,
  SearchQuestionsRequest,
  SearchQuestionsResponse,
} from '@/types/api/question';
import { StoryInfoType } from '@/types/api/stories';

// Enhanced search request interface
export interface EnhancedSearchRequest {
  search?: string;
  tags?: string[];
  userRole?: string;
  userIndustry?: string;
  type?: 'question' | 'story';
  // 🆕 NEW: Enhanced filtering options for comprehensive search
  questionTypes?: string[]; // ['IDEA_STARTER', 'DISCUSSIONS', 'NORMAL']
  categories?: string[]; // Category IDs for area of interest filtering
  areaOfInterestIds?: string[]; // Alternative to categories for consistency
  industryIds?: string[]; // Multiple industry selection
  occupationIds?: string[]; // Multiple occupation selection
  page?: number;
  limit?: number;
  // 🆕 NEW: Sorting support
  sortBy?: 'relevance' | 'date' | 'trending';
}

// Enhanced search response - compatible with existing QuestionInfoType
export interface EnhancedSearchResponse {
  data: (QuestionInfoType | (StoryInfoType & { type: 'story' }))[];
  totalCount: number;
  currentPage: number;
  limit: number;
  totalPages: number;
  source: 'elasticsearch' | 'traditional'; // Track which API was used
}

// Enhanced suggestions response
export interface EnhancedSuggestionsResponse {
  suggestions: string[];
  source: 'elasticsearch' | 'traditional';
}

// Remove empty strings/arrays/undefined from params before sending
const sanitizeSearchParams = (
  params: Record<string, unknown>
): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string') {
      const v = value.trim();
      if (v !== '') cleaned[key] = v;
      continue;
    }
    if (Array.isArray(value)) {
      const arr = value
        .map((v) => (typeof v === 'string' ? v.trim() : v))
        .filter((v) => v !== '' && v !== undefined && v !== null);
      if (arr.length > 0) cleaned[key] = arr;
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
};

/**
 * Transform ElasticSearch result to QuestionInfoType for existing components
 */
const transformToQuestionInfo = (item: any): QuestionInfoType => ({
  id: item.id,
  questionTitle: item.title, // Clean title for navigation
  questionDescription: item.description || null,
  createdDate: item.createdDate,
  questionType: item.questionType || 'NORMAL',
  modifiedDate: item.modifiedDate,
  slug: item.slug || '',
  isAnonymous: item.isAnonymous || false,
  isReported: item.isReported || false,
  storyCount: item.storyCount || 0,
  author: item.author,
  category: item.category || { id: '', name: '' },
});

/**
 * Transform ElasticSearch result to StoryInfoType for stories
 */
const transformToStoryInfo = (
  item: any
): StoryInfoType & { type: 'story' } => ({
  id: item.id,
  title: item.title || null, // Clean title for navigation
  theme: item.theme || null,
  author: item.author,
  authorId: item.author?.id,
  createdDate: item.createdDate,
  isAnonymous: item.isAnonymous || false,
  isReported: item.isReported || false,
  content: item.content || '',
  outcome: item.outcome || null,
  keywords: item.keywords || null,
  category: item.category || { id: '', name: '' },
  viewCount: item.viewCount || null,
  commentCount: 0, // Not available in ElasticSearch
  subStories: null,
  questionId: item.questionId || null,
  questionTitle: item.questionTitle || null,
  isMoreSubStoriesAvailable: false,
  question: item.questionSlug
    ? {
        id: item.questionId || '',
        slug: item.questionSlug,
        questionTitle: item.questionTitle || '',
        categoryId: item.category?.id || '',
      }
    : undefined,
  type: 'story' as const,
});

/**
 * Enhanced search with ElasticSearch and fallback to traditional API
 */
export const enhancedSearchQuestions = async (
  payload: EnhancedSearchRequest
): Promise<EnhancedSearchResponse> => {
  // Check if enhanced search is enabled
  const enhancedSearchEnabled =
    process.env.NEXT_PUBLIC_ENHANCED_SEARCH_ENABLED === 'true';
  const disableFallback =
    process.env.NEXT_PUBLIC_DISABLE_TRADITIONAL_FALLBACK === 'true';

  if (!enhancedSearchEnabled) {
    console.log('Enhanced search disabled, using traditional search');
    return await fallbackToTraditionalSearch(payload);
  }

  try {
    // Check if ElasticSearch service is available
    const serviceAvailable = await isSearchServiceAvailable();

    if (!serviceAvailable) {
      console.warn(
        'ElasticSearch service not available' +
          (disableFallback
            ? ' and fallback is disabled'
            : ', falling back to traditional search')
      );
      if (disableFallback) {
        throw new Error('Enhanced search unavailable and fallback disabled');
      }
      return await fallbackToTraditionalSearch(payload);
    }

    // ✅ SECURITY: Validate user authentication and get tenant ID
    const tenantValidation = validateUserTenant();

    // Use tenant ID if available, otherwise use default for public search
    const tenantId = tenantValidation.isValid
      ? tenantValidation.tenantId!
      : process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || 'public';

    if (!tenantValidation.isValid) {
      console.log(`Using public search mode with tenant: ${tenantId}`);
    }

    // Performance optimization: Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 Attempting enhanced search for tenant: ${tenantId}`);
    }

    const rawParams: Record<string, unknown> = {
      q: payload.search || '', // ✅ FIXED: Send empty string instead of undefined for trending results
      tags: payload.tags,
      userRole: payload.userRole,
      userIndustry: payload.userIndustry,
      type: payload.type,
      // 🆕 NEW: Enhanced filtering options
      questionTypes: payload.questionTypes,
      categories: payload.categories,
      areaOfInterestIds: payload.areaOfInterestIds,
      industryIds: payload.industryIds,
      occupationIds: payload.occupationIds,
      tenantId: tenantId, // ALWAYS include tenant ID
      page: payload.page || 1,
      pageSize: payload.limit || 20,
      // 🆕 NEW: Sorting support
      sortBy: payload.sortBy || 'relevance',
    };

    // 🔍 DEBUG: Log query characteristics for timeout debugging
    const queryLength = payload.search?.length || 0;
    const isLongQuery = queryLength > 100;

    if (isLongQuery) {
      console.log('🔍 LONG QUERY DEBUG - Extended timeout applied:', {
        queryLength: queryLength,
        timeout: 30000,
        queryPreview: payload.search?.substring(0, 100) + '...',
        timestamp: new Date().toISOString(),
      });
    }

    // Build bracketed array query string to mimic your working model
    const qs = (() => {
      const parts: string[] = [];

      const append = (key: string, value: string | number | boolean | undefined | null) => {
        if (value === undefined || value === null || value === '') return;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
      };

      const appendArrayBracketed = (key: string, values?: string[]) => {
        if (!values || values.length === 0) return;
        for (const v of values) {
          if (v !== undefined && v !== null && v !== '') {
            parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`);
          }
        }
      };

      append('q', rawParams.q as string);
      append('userRole', rawParams.userRole as string);
      append('userIndustry', rawParams.userIndustry as string);
      append('type', rawParams.type as string);
      append('tenantId', rawParams.tenantId as string);
      append('page', rawParams.page as number);
      append('pageSize', rawParams.pageSize as number);
      append('sortBy', rawParams.sortBy as string);

      appendArrayBracketed('tags', rawParams.tags as string[] | undefined);
      appendArrayBracketed('questionTypes', rawParams.questionTypes as string[] | undefined);
      appendArrayBracketed('categories', rawParams.categories as string[] | undefined);
      appendArrayBracketed('areaOfInterestIds', rawParams.areaOfInterestIds as string[] | undefined);
      appendArrayBracketed('industryIds', rawParams.industryIds as string[] | undefined);
      appendArrayBracketed('occupationIds', rawParams.occupationIds as string[] | undefined);

      return parts.join('&');
    })();

    const esResponse = await searchServiceAxios.get(`/search?${qs}`, {
      timeout: 30000, // Extended timeout for very long narrative queries
    });

    if (esResponse.data.success) {
      const esData = esResponse.data.data;

      // SECURITY: Validate all results belong to current tenant
      for (const item of esData.hits) {
        if (item.tenantId && item.tenantId !== tenantId) {
          throw new Error(
            `SECURITY VIOLATION: Search returned content from different tenant`
          );
        }
      }

      // Transform ElasticSearch results to existing types
      const transformedData = esData.hits.map((item: any) => {
        if (item.type === 'question') {
          const questionInfo = transformToQuestionInfo(item);
          // ✅ Add highlighted title for display (clean title already set for navigation)
          (questionInfo as any).questionTitleHighlighted =
            item.titleHighlighted;
          return questionInfo;
        } else {
          const storyInfo = transformToStoryInfo(item);
          // ✅ Add highlighted title for display (clean title already set for navigation)
          (storyInfo as any).titleHighlighted = item.titleHighlighted;
          return storyInfo;
        }
      });

      // Support both ES response shapes: number or { value }
      const totalValue =
        typeof esData.total === 'number'
          ? esData.total
          : (esData.total?.value ?? 0);

      return {
        data: transformedData,
        totalCount: totalValue,
        currentPage: payload.page || 1,
        limit: payload.limit || 20,
        totalPages: Math.ceil(totalValue / (payload.limit || 20)),
        source: 'elasticsearch',
      };
    } else {
      console.warn(
        'ElasticSearch returned unsuccessful response' +
          (disableFallback
            ? ' and fallback is disabled'
            : ', falling back to traditional search')
      );
      if (disableFallback) {
        throw new Error('Enhanced search unsuccessful and fallback disabled');
      }
      return await fallbackToTraditionalSearch(payload);
    }
  } catch (error) {
    console.error('ElasticSearch search failed:', error);
    if (disableFallback) {
      throw error;
    }
    return await fallbackToTraditionalSearch(payload);
  }
};

/**
 * Fallback to traditional search API when enhanced search is not available
 */
const fallbackToTraditionalSearch = async (
  payload: EnhancedSearchRequest
): Promise<EnhancedSearchResponse> => {
  try {
    console.log('🔄 Using traditional search API');

    // Map to traditional search request format
    const traditionalPayload: SearchQuestionsRequest = {
      search: payload.search || '',
      page: payload.page || 1,
      limit: payload.limit || 20,
    };

    const response = await publicContentAxios.get<SearchQuestionsResponse>(
      '/questions',
      { params: traditionalPayload }
    );

    console.log(
      `✅ Traditional search successful: ${response.data.data.length} results`
    );

    return {
      data: response.data.data, // Already in QuestionInfoType format
      totalCount: response.data.totalCount,
      currentPage: response.data.page, // Use 'page' property from PaginatedApiResponse
      limit: response.data.limit,
      totalPages: Math.ceil(response.data.totalCount / response.data.limit), // Calculate totalPages
      source: 'traditional',
    };
  } catch (error) {
    console.error('Traditional search also failed:', error);
    throw new Error('Both enhanced and traditional search failed');
  }
};

/**
 * Enhanced suggestions with ElasticSearch and fallback
 */
export const getEnhancedSuggestions = async (
  query: string,
  limit: number = 5
): Promise<EnhancedSuggestionsResponse> => {
  // Check if enhanced search is enabled
  const enhancedSearchEnabled =
    process.env.NEXT_PUBLIC_ENHANCED_SEARCH_ENABLED === 'true';

  if (!enhancedSearchEnabled) {
    return { suggestions: [], source: 'traditional' };
  }

  try {
    // Check if ElasticSearch service is available
    const serviceAvailable = await isSearchServiceAvailable();

    if (!serviceAvailable) {
      return { suggestions: [], source: 'traditional' };
    }

    const tenantId = getCurrentTenantIdWithFallback();

    const response = await searchServiceAxios.get('/search/suggestions', {
      params: { q: query, tenantId, limit },
      timeout: 3000, // Quick timeout for suggestions
    });

    return {
      suggestions: response.data.success ? response.data.data : [],
      source: 'elasticsearch',
    };
  } catch (error) {
    console.warn('Enhanced suggestions failed:', error);
    return { suggestions: [], source: 'traditional' };
  }
};

/**
 * Get search aggregations (tags, roles, industries, questionTypes, categories)
 */
export const getSearchAggregations = async (): Promise<{
  tags?: Array<{ key: string; count: number }>;
  userRoles?: Array<{ key: string; count: number }>;
  userIndustries?: Array<{ key: string; count: number }>;
  types?: Array<{ key: string; count: number }>;
  questionTypes?: Array<{ key: string; count: number }>;
  categories?: Array<{ key: string; count: number }>;
} | null> => {
  try {
    const serviceAvailable = await isSearchServiceAvailable();

    if (!serviceAvailable) {
      return null;
    }

    const tenantId = getCurrentTenantIdWithFallback();

    const response = await searchServiceAxios.get('/search/aggregations', {
      params: { tenantId },
      timeout: 5000,
    });

    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.warn('Search aggregations failed:', error);
    return null;
  }
};

// Cache for enhanced search status to avoid repeated checks
let enhancedSearchStatusCache: {
  available: boolean;
  source: 'elasticsearch' | 'traditional';
  responseTime?: number;
  timestamp: number;
} | null = null;

const CACHE_DURATION = 30000; // 30 seconds cache

/**
 * Check if enhanced search is available and working - with caching
 */
export const checkEnhancedSearchStatus = async (): Promise<{
  available: boolean;
  source: 'elasticsearch' | 'traditional';
  responseTime?: number;
}> => {
  // Return cached result if still valid
  if (
    enhancedSearchStatusCache &&
    Date.now() - enhancedSearchStatusCache.timestamp < CACHE_DURATION
  ) {
    return {
      available: enhancedSearchStatusCache.available,
      source: enhancedSearchStatusCache.source,
      responseTime: enhancedSearchStatusCache.responseTime,
    };
  }

  const startTime = Date.now();

  try {
    const serviceAvailable = await isSearchServiceAvailable();
    const responseTime = Date.now() - startTime;

    const result = {
      available: serviceAvailable,
      source: (serviceAvailable ? 'elasticsearch' : 'traditional') as
        | 'elasticsearch'
        | 'traditional',
      responseTime,
    };

    // Cache the result
    enhancedSearchStatusCache = {
      ...result,
      timestamp: Date.now(),
    };

    return result;
  } catch (error) {
    const result = {
      available: false,
      source: 'traditional' as const,
      responseTime: Date.now() - startTime,
    };

    // Cache the result even on error
    enhancedSearchStatusCache = {
      ...result,
      timestamp: Date.now(),
    };

    return result;
  }
};
